import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import pnlService from "../../Services/pnlService";

const MONTHS_ORDER = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const CATEGORIES = [
  { label: "Checkup", key: "checkupAmount" },
  { label: "Medicines", key: "medicineAmount" },
  { label: "Services", key: "servicesAmount" },
  { label: "Pharmacy", key: "pharmacyAmount" },
  { label: "Radiology", key: "radiologyAmount" },
  { label: "Laboratory", key: "laboratoryAmount" },
];

// Month | categories | Total
const colWidths = [
  "130px",
  ...CATEGORIES.map(() => "105px"),
  "120px",
];
const tableMinWidth = colWidths.reduce((sum, w) => sum + parseInt(w, 10), 0);

const ColGroup = () => (
  <colgroup>
    {colWidths.map((w, i) => (
      <col key={i} style={{ width: w, minWidth: w }} />
    ))}
  </colgroup>
);

/* ---------- data helpers ---------- */

// Response se list nikalta hai: pehle di gayi keys, warna pehla array
const pickList = (res, keys = []) => {
  for (const key of keys) {
    if (Array.isArray(res?.[key])) return res[key];
  }
  return Object.values(res || {}).find((v) => Array.isArray(v)) || [];
};

// Ek API call safe tareeqe se: method na ho ya fail ho to [] return
const safeFetch = async (methodName, keys) => {
  const fn = pnlService?.[methodName];
  if (typeof fn !== "function") {
    console.warn(`pnlService.${methodName} exist nahi karta`);
    return [];
  }
  try {
    const res = await fn.call(pnlService);
    return pickList(res, keys);
  } catch (error) {
    console.error(`${methodName} fail hua:`, error);
    return [];
  }
};

// Row mein se pehli maujood amount field uthata hai
const pickAmount = (row, fields) => {
  for (const f of fields) {
    if (row?.[f] !== undefined && row?.[f] !== null) return parseFloat(row[f]) || 0;
  }
  return 0;
};

const findMonth = (list, month, year) =>
  list.find(
    (r) => r.month_name === month && Number(r.year) === Number(year)
  );

  const aggregateDailyByMonth = (
  records,
  dateField,
  salesField,
  amountField,
  amountKey
) => {
  const monthlyData = {};

  records.forEach((item) => {
    const rawDate = item?.[dateField];

    if (!rawDate) return;

    const [year, month] = String(rawDate).split("T")[0].split("-");

    if (!year || !month) return;

    const monthName = MONTHS_ORDER[Number(month) - 1];

    if (!monthName) return;

    const key = `${year}-${month}`;

    if (!monthlyData[key]) {
      monthlyData[key] = {
        month_name: monthName,
        year: Number(year),
        total_sales: 0,
        [amountKey]: 0,
      };
    }

    monthlyData[key].total_sales += Number(item[salesField]) || 0;
    monthlyData[key][amountKey] += Number(item[amountField]) || 0;
  });

  return Object.values(monthlyData);
};

const ByDate = () => {
  const currentYear = new Date().getFullYear();

  const [medicines, setMedicines] = useState([]);
  const [checkup, setCheckup] = useState([]);
  const [pharmacy, setPharmacy] = useState([]);
  const [services, setServices] = useState([]);
  const [radiology, setRadiology] = useState([]);
  const [laboratory, setLaboratory] = useState([]);

  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [expandedMonths, setExpandedMonths] = useState({});

  const fmt = (n) => {
    const v = isNaN(n) ? 0 : Math.round(parseFloat(n) * 100) / 100;
    return v.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /* ---------- data fetching ---------- */

  useEffect(() => {
  const fetchAll = async () => {
    try {
      setLoading(true);

      const [
        med,
        chk,
        pharm,
        serv,
        radiologyResponse,
        laboratoryResponse,
      ] = await Promise.all([
        safeFetch("fetchMedicinebyMonth", ["Medicines"]),
        safeFetch("fetchCheckupbyMonth", ["Checkup"]),
        safeFetch("fetchPharmacybyMonth", ["Pharmacy"]),
        safeFetch("fetchServicesbyMonth", ["Services"]),

        pnlService.fetchDailyRadiologyStats(),

        pnlService.fetchDailyLaboratoryStats(),
      ]);

      setMedicines(med);
      setCheckup(chk);
      setPharmacy(pharm);
      setServices(serv);

      // Radiology daily API -> monthly totals
      const radiologyRecords =
        radiologyResponse?.dayradiologysales || [];

      const monthlyRadiology = aggregateDailyByMonth(
        radiologyRecords,
        "new_date",
        "total_rad_sales",
        "total_recieved",
        "total_radiology_sale"
      );

      setRadiology(monthlyRadiology);

      // Laboratory daily API -> monthly totals
      const laboratoryRecords =
        laboratoryResponse?.daylabsales || [];

      const monthlyLaboratory = aggregateDailyByMonth(
        laboratoryRecords,
        "date",
        "total_lab_orders",
        "total_fee_recieved",
        "total_laboratory_sale"
      );

      setLaboratory(monthlyLaboratory);

    } catch (error) {
      console.error("Error fetching yearly income data:", error);
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  fetchAll();
}, []);

  /* ---------- combine ---------- */

  const combinedData = MONTHS_ORDER.map((month) => {
    const med = findMonth(medicines, month, selectedYear);
    const chk = findMonth(checkup, month, selectedYear);
    const srv = findMonth(services, month, selectedYear);
    const ph = findMonth(pharmacy, month, selectedYear);
    const rad = findMonth(radiology, month, selectedYear);
    const lab = findMonth(laboratory, month, selectedYear);

    const checkupAmount = pickAmount(chk, ["total_checkup_fee"]);
    const medicineAmount = pickAmount(med, ["total_medicines_sale"]);
    const servicesAmount = pickAmount(srv, [
      "total_services_sale",
      "total_medicines_sale",
    ]);
    const pharmacyAmount = pickAmount(ph, ["total_amount"]);
    const radiologyAmount = pickAmount(rad, [
      "total_radiology_sale",
      "total_radiology_fee",
      "total_amount",
    ]);
    const laboratoryAmount = pickAmount(lab, [
      "total_laboratory_sale",
      "total_laboratory_fee",
      "total_lab_sale",
      "total_amount",
    ]);

    const total =
      checkupAmount +
      medicineAmount +
      servicesAmount +
      pharmacyAmount +
      radiologyAmount +
      laboratoryAmount;

    return {
      month,
      checkupAmount,
      medicineAmount,
      servicesAmount,
      pharmacyAmount,
      radiologyAmount,
      laboratoryAmount,
      total,
    };
  }).filter((item) => item.total > 0);

  const calculateTotal = (key) =>
    combinedData.reduce((sum, item) => sum + (item[key] || 0), 0);

  const grandTotal = calculateTotal("total");

  const toggleMonth = (month) => {
    setExpandedMonths((prev) => ({ ...prev, [month]: !prev[month] }));
  };

  /* ---------- UI ---------- */

  return (
    <div className="p-3 sm:p-4 font-sans">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
          Yearly Income Summary
        </p>
        <select
          className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
        >
          {[2024, 2025, 2026].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2 sm:gap-3 mb-4">
        {CATEGORIES.map(({ label, key }) => (
          <div
            key={label}
            className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2.5"
          >
            <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
              {label}
            </p>
            <p className="text-base font-semibold text-gray-800">
              {fmt(calculateTotal(key))}
            </p>
          </div>
        ))}
        <div className="bg-gray-100 rounded-lg px-3 py-2.5 border-t-2 border-[#F07F1C] col-span-2 sm:col-span-3 lg:col-span-2 xl:col-span-1">
          <p className="text-[10px] tracking-wide text-gray-400 uppercase mb-1">
            Grand Total
          </p>
          <p className="text-base font-bold text-[#F07F1C]">{fmt(grandTotal)}</p>
        </div>
      </div>

      {/* ══════════ MOBILE VIEW (< 640px) ══════════ */}
      <div className="block sm:hidden">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-xs">Loading...</div>
        ) : (
          <>
            <div className="space-y-2">
              {combinedData.map((item) => {
                const isOpen = !!expandedMonths[item.month];
                return (
                  <div
                    key={item.month}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => toggleMonth(item.month)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 text-left"
                    >
                      <span className="text-[12px] font-medium text-gray-700">
                        {item.month}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-[#F07F1C]">
                          {fmt(item.total)}
                        </span>
                        <svg
                          className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-3 py-2 bg-white divide-y divide-gray-100">
                        {CATEGORIES.map(({ label, key }) => (
                          <div
                            key={label}
                            className="flex items-center justify-between py-2"
                          >
                            <span className="text-[12px] text-gray-500 w-20">
                              {label}
                            </span>
                            <span className="text-[12px] font-medium text-gray-800">
                              {fmt(item[key])}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mobile Grand Total Footer */}
            <div className="mt-3 bg-[#215187] rounded-xl px-3 py-3">
              {CATEGORIES.map(({ label, key }) => (
                <div
                  key={label}
                  className="flex items-center justify-between py-1.5 border-b border-white/10 last:border-0"
                >
                  <span className="text-[12px] text-gray-300">{label}</span>
                  <span className="text-[12px] text-gray-100 font-medium">
                    {fmt(calculateTotal(key))}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 mt-1 border-t border-white/20">
                <span className="text-[12px] font-medium text-gray-100">
                  Grand Total
                </span>
                <span className="text-[14px] font-bold text-[#F07F1C]">
                  {fmt(grandTotal)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ══════════ DESKTOP VIEW (>= 640px) ══════════ */}
      <div className="hidden sm:block border rounded-xl overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: "520px" }}>
          <table
            className="w-full text-sm"
            style={{
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: 0,
              minWidth: tableMinWidth,
            }}
          >
            <ColGroup />

            {/* STICKY HEADER */}
            <thead>
              <tr>
                <th className="sticky top-0 z-20 bg-[#215187] text-left px-4 py-2.5 text-[12px] text-gray-100 uppercase border-r border-white/10">
                  Month
                </th>
                {CATEGORIES.map(({ label }) => (
                  <th
                    key={label}
                    className="sticky top-0 z-20 bg-[#215187] text-center px-4 py-2.5 text-[12px] text-gray-100 uppercase border-r border-white/10"
                  >
                    {label}
                  </th>
                ))}
                <th className="sticky top-0 z-20 bg-[#215187] text-right px-4 py-2.5 text-[12px] text-gray-100 uppercase">
                  Total
                </th>
              </tr>
            </thead>

            {/* BODY */}
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={colWidths.length}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : combinedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={colWidths.length}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    No records found for this year.
                  </td>
                </tr>
              ) : (
                combinedData.map((item, i) => (
                  <tr
                    key={item.month}
                    className={`border-b border-gray-100 hover:bg-green-50 transition-colors ${
                      i % 2 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="px-4 py-2 border-r border-gray-100 text-[12px] text-gray-600">
                      {item.month}
                    </td>
                    {CATEGORIES.map(({ label, key }) => (
                      <td
                        key={label}
                        className="px-4 py-2 text-center border-r border-gray-100 text-[12px] text-gray-700"
                      >
                        {fmt(item[key])}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right text-[12px] font-semibold text-gray-800">
                      {fmt(item.total)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {/* STICKY FOOTER */}
            <tfoot>
              <tr>
                <td className="sticky bottom-0 z-20 bg-[#215187] text-gray-100 px-4 py-2.5 text-left text-[12px] font-medium border-t border-white/10">
                  Grand Total
                </td>
                {CATEGORIES.map(({ label, key }) => (
                  <td
                    key={label}
                    className="sticky bottom-0 z-20 bg-[#215187] text-gray-100 px-4 py-2.5 text-center text-[12px] font-medium border-t border-white/10"
                  >
                    {fmt(calculateTotal(key))}
                  </td>
                ))}
                <td className="sticky bottom-0 z-20 bg-[#215187] text-gray-100 px-4 py-2.5 text-right text-[12px] font-semibold border-t border-white/10">
                  {fmt(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ByDate;